import { z } from "zod";
import { environmentSchema } from "./context.js";
const micros = z.number().int().min(0).max(1_000_000_000_000);
/** A review reference, never a credential, signed URL, prompt or provider response. */
const reference = z
    .string()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.:/-]*$/);
export const researchLeaseUsageObservationSchema = z
    .object({
    providerRequestId: reference,
    occurredAt: z.iso.datetime({ offset: true }),
    groundedCalls: z.number().int().min(0).max(10_000),
    providerCostUsdMicros: micros,
})
    .strict();
export const researchLeaseReconciliationCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    expectedReservationSha256: z.string().regex(/^[a-f0-9]{64}$/),
    evidence: z
        .object({
        provider: z.literal("GOOGLE_VERTEX_AI"),
        accountReference: reference,
        recordReference: reference,
        contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
        determination: z.enum(["METERED_USAGE", "PROVIDER_CONFIRMED_NO_USAGE"]),
        reviewed: z.literal(true),
    })
        .strict(),
    observations: z.array(researchLeaseUsageObservationSchema).min(1).max(100),
    reason: z.string().trim().min(10).max(500),
    /** Prior unit spend cannot be reliably attributed to an individual old lease.
     * Retain all of it and add this verified cost as a conservative budget floor.
     * Neither this floor nor the previous list-price estimate is an invoice. */
    reportedSpendPolicy: z.literal("PRESERVE_AND_ADD_VERIFIED_COST"),
})
    .strict()
    .superRefine((value, ctx) => {
    if (new Set(value.observations.map((v) => v.providerRequestId)).size !== value.observations.length)
        ctx.addIssue({
            code: "custom",
            path: ["observations"],
            message: "Provider request references must be unique",
        });
    const calls = value.observations.reduce((n, v) => n + v.groundedCalls, 0);
    const cost = value.observations.reduce((n, v) => n + v.providerCostUsdMicros, 0);
    if (cost > 1_000_000_000_000 || calls > 10_000)
        ctx.addIssue({
            code: "custom",
            path: ["observations"],
            message: "Receipt total exceeds its bounded limit",
        });
    if ((calls === 0 && cost === 0) !==
        (value.evidence.determination === "PROVIDER_CONFIRMED_NO_USAGE"))
        ctx.addIssue({
            code: "custom",
            path: ["evidence", "determination"],
            message: "Zero usage requires explicit provider confirmation",
        });
});
export const researchLeaseReconciliationSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.uuid(),
    leaseId: z.uuid(),
    organizationId: z.uuid(),
    environment: environmentSchema,
    actorId: z.uuid(),
    createdAt: z.iso.datetime({ offset: true }),
    correlationId: z.string(),
    idempotencyKey: z.string(),
    groundedCalls: z.number().int().min(0),
    providerCostUsdMicros: micros,
    budgetAdditionalUsdMicros: micros,
    review: researchLeaseReconciliationCreateSchema,
})
    .strict();
export const researchGroundedLeaseSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.uuid(),
    organizationId: z.uuid(),
    environment: environmentSchema,
    unitId: z.uuid(),
    sweepId: z.uuid(),
    claimedAt: z.iso.datetime({ offset: true }),
    settledAt: z.iso.datetime({ offset: true }).nullable(),
    callAllowance: z.number().int().positive(),
    estimatedHoldUsdMicros: micros,
    workerReportedCalls: z.number().int().min(0).nullable(),
    unitReportedSpendUsdMicros: micros,
    reportedSpendAttribution: z.literal("UNIT_AGGREGATE_NOT_LEASE_ATTRIBUTED"),
    state: z.enum(["ACTIVE", "UNKNOWN_USAGE", "WORKER_SETTLED", "RECONCILED"]),
    reservationSha256: z.string().regex(/^[a-f0-9]{64}$/),
    reconciliation: researchLeaseReconciliationSchema.nullable(),
})
    .strict();
//# sourceMappingURL=research-lease-reconciliation.js.map